import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router";
import { ThemeProvider } from "./components/ui/theme-provider";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SearchProvider } from './contexts/SearchContext';
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { TokenErrorBoundary } from "./components/TokenErrorBoundary";
import { useEffect, lazy, Suspense } from "react";
import { safeDecodeToken } from "./utils/jwt";

// Lazy load all pages for code splitting
const Login = lazy(() => import("./pages/Login").then(module => ({ default: module.Login })));
const Home = lazy(() => import("./pages/Home").then(module => ({ default: module.Home })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").then(module => ({ default: module.PrivacyPolicy })));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SalarySettings = lazy(() => import('./pages/SalarySettings').then(module => ({ default: module.SalarySettings })));
const ReadOnlyExpertView = lazy(() => import('./components/ReadOnlyExpertView').then(module => ({ default: module.ReadOnlyExpertView })));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
  </div>
);

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated, setUserRole, setDisplayName } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const accessToken = params.get('accessToken');
    const refreshToken = params.get('refreshToken');

    if (accessToken && refreshToken) {
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Decode JWT to get role
      const payload = safeDecodeToken(accessToken);
      if (payload?.roles) {
        setUserRole(payload.roles);
      }
      if (payload?.displayName) {
        localStorage.setItem('displayName', payload.displayName);
        setDisplayName(payload.displayName);
      }

      // Update authentication state
      setIsAuthenticated(true);

      navigate('/', { replace: true });
    } else {

      navigate('/login', { replace: true });
    }
  }, [location, navigate, setIsAuthenticated, setUserRole, setDisplayName]);

  return null;
}

function AppContent() {

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Layout isLoginPage={true}><Login /></Layout>} />
        <Route path="/callback" element={<AuthCallback />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <Home />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/general"
          element={
            <ProtectedRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/salary"
          element={
            <ProtectedRoute>
              <Layout>
                <SalarySettings />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/expert/:id" 
          element={
            <ProtectedRoute>
              <Layout>
                <ReadOnlyExpertView />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/privacy-policy" 
          element={
            <Layout isLoginPage={true} >
              <PrivacyPolicy />
            </Layout>
          } 
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <TokenErrorBoundary>
      <AuthProvider>
        <SearchProvider>
          <ThemeProvider defaultTheme="light" storageKey="ui-theme">
            <Router>
              <AppContent />
            </Router>
            <Toaster />
          </ThemeProvider>
        </SearchProvider>
      </AuthProvider>
    </TokenErrorBoundary>
  );
}

export default App;
